import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, CalendarDays } from "lucide-react";
import { Reveal, SectionLabel } from "../components/Reveal";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [];
for (let h = 19; h <= 23; h++) {
  TIME_SLOTS.push(`${h}:00`);
  TIME_SLOTS.push(`${h}:30`);
}

const initialForm = { name: "", phone: "", email: "", guests: 2, date: "", time: "20:00", seating: "dentro", notes: "" };

export default function Prenota() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const { t, lang } = useLang();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const isMonday = (dateStr) => {
    if (!dateStr) return false;
    return new Date(`${dateStr}T12:00:00`).getDay() === 1;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isMonday(form.date)) {
      toast.error(t("book.mondayErr"));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/reservations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, guests: Number(form.guests), lang }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setDone(data);
      toast.success(t("book.okToast"));
    } catch {
      toast.error(t("book.errToast"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full h-[52px] bg-pine border border-border text-foreground px-4 rounded-sm placeholder:text-fog/50 focus:outline-none focus:ring-1 focus:ring-neon focus:border-neon transition-colors duration-300";

  return (
    <main className="pt-20" data-testid="prenota-page">
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionLabel number="·">{t("book.label")}</SectionLabel>
            <h1 className="font-display uppercase leading-[0.9] tracking-tight text-6xl sm:text-7xl text-foreground">
              {t("book.t1")} <span className="text-neon">{t("book.t2")}</span>
            </h1>
            <p className="text-fog text-lg mt-6 leading-relaxed max-w-md">{t("book.intro")}</p>
          </Reveal>

          <Reveal delay={0.15} className="mt-10">
            <div className="border border-neon/20 bg-pine p-6 space-y-4 rounded-sm">
              <div className="flex items-center gap-3 text-fog">
                <CalendarDays size={20} className="text-neon" />
                <span>{t("book.hoursLine")}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                className="border border-neon/40 bg-pine p-10 text-center rounded-sm"
                data-testid="prenota-success"
              >
                <CheckCircle2 size={48} className="text-neon mx-auto mb-6" />
                <h2 className="font-head font-bold uppercase tracking-tight text-3xl text-foreground">{t("book.okTitle")}</h2>
                <p className="text-neon text-xl font-head mt-2">{t("book.okBella")}</p>
                <p className="text-fog mt-4 leading-relaxed">
                  {t("book.okThanks")} {done.name}! {t("book.okA")}{" "}
                  <span className="text-neon font-semibold">{done.guests} {t(done.guests === 1 ? "book.persona" : "book.persone")}</span>{" "}
                  {t("book.okB")} <span className="text-neon font-semibold">{done.date}</span> {t("book.okC")}{" "}
                  <span className="text-neon font-semibold">{done.time}</span>.{" "}
                  {t("book.okD")} {done.email}{t("book.okE")}
                </p>
                <p className="text-fog/80 text-sm mt-3">{t("book.okEmail")}</p>
                <p className="text-fog/80 text-sm mt-2 border-t border-neon/10 pt-3">{t("book.okPolicy")}</p>
                <button
                  onClick={() => { setDone(null); setForm(initialForm); }}
                  data-testid="prenota-new-btn"
                  className="mt-8 border border-neon text-neon font-head font-bold text-sm tracking-[0.2em] uppercase px-8 py-3 rounded-full hover:bg-neon hover:text-ink transition-colors duration-300"
                >
                  {t("book.new")}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="border border-neon/20 bg-pine/60 p-8 sm:p-10 space-y-6 rounded-sm" data-testid="prenota-form">
                <div>
                  <label htmlFor="prenota-name" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.name")}</label>
                  <input id="prenota-name" data-testid="prenota-input-name" required value={form.name} onChange={set("name")}
                    placeholder={t("book.namePh")} className={inputCls} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="prenota-phone" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.phone")}</label>
                    <input id="prenota-phone" data-testid="prenota-input-phone" required type="tel" value={form.phone} onChange={set("phone")}
                      placeholder="340 000 0000" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="prenota-email" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.email")}</label>
                    <input id="prenota-email" data-testid="prenota-input-email" required type="email" value={form.email} onChange={set("email")}
                      placeholder={t("book.emailPh")} className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="prenota-guests" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.guests")}</label>
                    <select id="prenota-guests" data-testid="prenota-input-guests" value={form.guests} onChange={set("guests")} className={inputCls}>
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1} {t(i === 0 ? "book.persona" : "book.persone")}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="prenota-date" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.date")}</label>
                    <input id="prenota-date" data-testid="prenota-input-date" required type="date" value={form.date} onChange={set("date")}
                      min={new Date().toISOString().split("T")[0]} className={`${inputCls} appearance-none [color-scheme:dark]`} />
                  </div>
                  <div>
                    <label htmlFor="prenota-time" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.time")}</label>
                    <select id="prenota-time" data-testid="prenota-input-time" value={form.time} onChange={set("time")} className={inputCls}>
                      {TIME_SLOTS.map((ts) => (
                        <option key={ts} value={ts}>{ts}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <span className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.seating")}</span>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ v: "dentro", label: t("book.inside") }, { v: "fuori", label: t("book.outside") }].map((o) => (
                      <button
                        type="button"
                        key={o.v}
                        onClick={() => setForm({ ...form, seating: o.v })}
                        data-testid={`prenota-seating-${o.v}`}
                        className={`font-head font-semibold text-sm tracking-wide uppercase px-4 py-3.5 rounded-sm border transition-colors duration-300 ${
                          form.seating === o.v
                            ? "border-neon bg-neon/10 text-neon"
                            : "border-border text-fog hover:border-neon/40 hover:text-foreground"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="prenota-notes" className="block text-xs tracking-[0.25em] uppercase font-head font-semibold text-fog mb-2">{t("book.notes")}</label>
                  <textarea id="prenota-notes" data-testid="prenota-input-notes" rows={3} value={form.notes} onChange={set("notes")}
                    placeholder={t("book.notesPh")} className={`${inputCls} h-auto py-3.5 resize-none`} />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="prenota-submit-btn"
                  className="w-full bg-neon text-ink font-head font-bold tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
                >
                  {loading ? t("book.sending") : t("book.submit")}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
